import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  CheckIcon, 
  XMarkIcon,
  TruckIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  EyeIcon,
  ClipboardDocumentIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const Transfers = () => {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferType, setTransferType] = useState('depot_to_depot');
  const [depots, setDepots] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    source_depot_id: '',
    destination_depot_id: '',
    source_agent_id: '',
    destination_agent_id: '',
    sales_agent_id: '',
    items: [],
    notes: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    transfer_type: '',
    from_date: '',
    to_date: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });
  const [summary, setSummary] = useState({
    total_transfers: 0,
    total_cases: 0,
    completed: 0,
    pending: 0
  });

  useEffect(() => {
    fetchTransfers();
    fetchDepots();
    fetchProducts();
    if (user?.user_type !== 'sales_agent') {
      fetchSalesAgents();
    }
  }, [pagination.page, pagination.per_page, filters]);

  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        status: filters.status || undefined,
        transfer_type: filters.transfer_type || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined
      };

      const response = await api.get('/transfers/', { params });
      setTransfers(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));

      // Update summary
      const items = response.data.items || [];
      setSummary({
        total_transfers: response.data.total,
        total_cases: items.reduce((sum, t) => sum + t.quantity_cases, 0),
        completed: items.filter(t => t.status === 'completed').length,
        pending: items.filter(t => t.status === 'pending').length
      });
    } catch (error) {
      toast.error('Failed to fetch transfers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await api.get('/depot/', { params: { per_page: 1000 } });
      setDepots(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch depots:', error);
    }
  };

  const fetchSalesAgents = async () => {
    try {
      const response = await api.get('/users/', {
        params: { user_type: 'sales_agent', per_page: 1000 }
      });
      setSalesAgents(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch sales agents:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/', { params: { per_page: 1000 } });
      setProducts(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleCreateTransfer = async () => {
    try {
      let endpoint = '';
      let payload = {};

      if (transferType === 'depot_to_depot') {
        endpoint = '/transfers/depot-to-depot';
        payload = {
          source_depot_id: formData.source_depot_id,
          destination_depot_id: formData.destination_depot_id,
          items: formData.items.map(item => ({
            product_id: item.product_id,
            quantity_cases: item.quantity_cases
          })),
          notes: formData.notes
        };
      } else if (transferType === 'agent_to_depot') {
        endpoint = '/transfers/agent-to-depot';
        payload = {
          sales_agent_id: formData.sales_agent_id,
          destination_depot_id: formData.destination_depot_id,
          items: formData.items.map(item => ({
            product_id: item.product_id,
            quantity_cases: item.quantity_cases,
            batch_id: item.batch_id
          })),
          notes: formData.notes
        };
      } else {
        endpoint = '/transfers/agent-to-agent';
        payload = {
          source_agent_id: formData.source_agent_id,
          destination_agent_id: formData.destination_agent_id,
          items: formData.items.map(item => ({
            product_id: item.product_id,
            quantity_cases: item.quantity_cases,
            batch_id: item.batch_id
          })),
          notes: formData.notes
        };
      }

      await api.post(endpoint, payload);
      toast.success('Transfer created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create transfer');
    }
  };

  const handleApproveTransfer = async () => {
    try {
      await api.post(`/transfers/${selectedTransfer.id}/approve`);
      toast.success('Transfer approved and completed');
      setShowApproveDialog(false);
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve transfer');
    }
  };

  const handleRejectTransfer = async () => {
    try {
      await api.post(`/transfers/${selectedTransfer.id}/reject`);
      toast.success('Transfer rejected');
      setShowRejectDialog(false);
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject transfer');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: '',
        quantity_cases: 1,
        batch_id: ''
      }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const resetForm = () => {
    setFormData({
      source_depot_id: '',
      destination_depot_id: '',
      source_agent_id: '',
      destination_agent_id: '',
      sales_agent_id: '',
      items: [],
      notes: ''
    });
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get('/transfers/reports', {
        params: {
          from_date: filters.from_date,
          to_date: filters.to_date,
          transfer_type: filters.transfer_type,
          format
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transfer_report_${format(new Date(), 'yyyyMMdd')}.${format === 'excel' ? 'xlsx' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const canApproveReject = () => {
    return user?.user_type === 'owner' || user?.user_type === 'manager';
  };

  const getTransferTypeIcon = (type) => {
    switch(type) {
      case 'depot_to_depot':
        return <BuildingOfficeIcon className="h-4 w-4 text-blue-500" />;
      case 'agent_to_depot':
        return <ArrowPathIcon className="h-4 w-4 text-green-500" />;
      case 'agent_to_agent':
        return <ArrowsRightLeftIcon className="h-4 w-4 text-purple-500" />;
      default:
        return <TruckIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const columns = [
    {
      header: 'Transfer #',
      accessorKey: 'transfer_number',
      cell: ({ getValue }) => (
        <span className="font-medium text-primary-600">{getValue()}</span>
      )
    },
    {
      header: 'Type',
      accessorKey: 'transfer_type',
      cell: ({ getValue, row }) => (
        <div className="flex items-center space-x-1">
          {getTransferTypeIcon(getValue())}
          <span className="capitalize text-sm">{getValue().replace('_', ' ')}</span>
        </div>
      )
    },
    {
      header: 'Date',
      accessorKey: 'transfer_date',
      cell: ({ getValue }) => format(new Date(getValue()), 'dd MMM yyyy')
    },
    {
      header: 'Product',
      accessorKey: 'product_name',
      cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>
    },
    {
      header: 'Quantity',
      cell: ({ row }) => (
        <div>
          <div>{row.original.quantity_cases} cases</div>
          <div className="text-xs text-gray-500">{row.original.quantity_bottles} bottles</div>
        </div>
      )
    },
    {
      header: 'From',
      cell: ({ row }) => (
        <div className="text-sm">
          {row.original.source_type === 'depot' ? 
            <div className="flex items-center"><BuildingOfficeIcon className="h-4 w-4 mr-1 text-gray-400" /> Depot</div> :
            <div className="flex items-center"><UserGroupIcon className="h-4 w-4 mr-1 text-gray-400" /> Sales Agent</div>
          }
        </div>
      )
    },
    {
      header: 'To',
      cell: ({ row }) => (
        <div className="text-sm">
          {row.original.destination_type === 'depot' ? 
            <div className="flex items-center"><BuildingOfficeIcon className="h-4 w-4 mr-1 text-gray-400" /> Depot</div> :
            <div className="flex items-center"><UserGroupIcon className="h-4 w-4 mr-1 text-gray-400" /> Sales Agent</div>
          }
        </div>
      )
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          {row.original.status === 'pending' && canApproveReject() && (
            <>
              <button
                onClick={() => {
                  setSelectedTransfer(row.original);
                  setShowApproveDialog(true);
                }}
                className="text-green-600 hover:text-green-900"
                title="Approve"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  setSelectedTransfer(row.original);
                  setShowRejectDialog(true);
                }}
                className="text-red-600 hover:text-red-900"
                title="Reject"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  const canCreateTransfer = () => {
    return user?.user_type === 'owner' || user?.user_type === 'manager';
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Stock Transfers</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage stock transfers between depots, from agents to depots, and between agents.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            type="button"
            onClick={() => handleExport('excel')}
            className="btn-secondary"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export
          </button>
          {canCreateTransfer() && (
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Transfer
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <ClipboardDocumentIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Transfers</dt>
                  <dd className="text-lg font-medium text-gray-900">{summary.total_transfers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <TruckIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Cases Transferred</dt>
                  <dd className="text-lg font-medium text-gray-900">{summary.total_cases}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <CheckIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">{summary.completed}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <ArrowPathIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                  <dd className="text-lg font-medium text-gray-900">{summary.pending}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Transfer Type</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.transfer_type}
                onChange={(e) => setFilters({...filters, transfer_type: e.target.value})}
              >
                <option value="">All</option>
                <option value="depot_to_depot">Depot to Depot</option>
                <option value="agent_to_depot">Agent to Depot</option>
                <option value="agent_to_agent">Agent to Agent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">From Date</label>
              <input
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.from_date}
                onChange={(e) => setFilters({...filters, from_date: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Date</label>
              <input
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.to_date}
                onChange={(e) => setFilters({...filters, to_date: e.target.value})}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={fetchTransfers}
              className="btn-primary"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Transfers Table */}
      <div className="mt-8">
        <DataTable
          data={transfers}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Create Transfer Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create Stock Transfer"
        size="lg"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleCreateTransfer();
        }}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Transfer Type *</label>
              <select
                required
                className="form-input"
                value={transferType}
                onChange={(e) => setTransferType(e.target.value)}
              >
                <option value="depot_to_depot">Depot to Depot</option>
                <option value="agent_to_depot">Sales Agent to Depot</option>
                <option value="agent_to_agent">Sales Agent to Sales Agent</option>
              </select>
            </div>

            {transferType === 'depot_to_depot' ? (
              <>
                <div>
                  <label className="form-label">Source Depot *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.source_depot_id}
                    onChange={(e) => setFormData({...formData, source_depot_id: e.target.value})}
                  >
                    <option value="">Select Source Depot</option>
                    {depots.map(depot => (
                      <option key={depot.id} value={depot.id}>{depot.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Destination Depot *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.destination_depot_id}
                    onChange={(e) => setFormData({...formData, destination_depot_id: e.target.value})}
                  >
                    <option value="">Select Destination Depot</option>
                    {depots.map(depot => (
                      <option key={depot.id} value={depot.id}>{depot.name}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : transferType === 'agent_to_depot' ? (
              <>
                <div>
                  <label className="form-label">Sales Agent *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.sales_agent_id}
                    onChange={(e) => setFormData({...formData, sales_agent_id: e.target.value})}
                  >
                    <option value="">Select Sales Agent</option>
                    {salesAgents.map(agent => (
                      <option key={agent.id} value={agent.profile?.id}>
                        {agent.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Destination Depot *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.destination_depot_id}
                    onChange={(e) => setFormData({...formData, destination_depot_id: e.target.value})}
                  >
                    <option value="">Select Destination Depot</option>
                    {depots.map(depot => (
                      <option key={depot.id} value={depot.id}>{depot.name}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="form-label">Source Sales Agent *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.source_agent_id}
                    onChange={(e) => setFormData({...formData, source_agent_id: e.target.value})}
                  >
                    <option value="">Select Source Sales Agent</option>
                    {salesAgents.map(agent => (
                      <option key={agent.id} value={agent.profile?.id}>
                        {agent.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Destination Sales Agent *</label>
                  <select
                    required
                    className="form-input"
                    value={formData.destination_agent_id}
                    onChange={(e) => setFormData({...formData, destination_agent_id: e.target.value})}
                  >
                    <option value="">Select Destination Sales Agent</option>
                    {salesAgents.map(agent => (
                      <option key={agent.id} value={agent.profile?.id}>
                        {agent.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="form-label mb-2">Items *</label>
              {formData.items.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <select
                    required
                    className="flex-1 form-input"
                    value={item.product_id}
                    onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                  >
                    <option value="">Select Product</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} ({product.bottles_per_case} bottles/case)
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    required
                    className="w-32 form-input"
                    placeholder="Cases"
                    value={item.quantity_cases}
                    onChange={(e) => updateItem(index, 'quantity_cases', parseInt(e.target.value))}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-800 px-2"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addItem}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                + Add Item
              </button>
            </div>

            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={formData.items.length === 0}
              >
                Create Transfer
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Approve Confirmation */}
      <ConfirmDialog
        isOpen={showApproveDialog}
        onClose={() => setShowApproveDialog(false)}
        onConfirm={handleApproveTransfer}
        title="Approve Transfer"
        message={`Are you sure you want to approve transfer ${selectedTransfer?.transfer_number}? This will move the stock immediately.`}
        confirmText="Approve"
        type="success"
      />

      {/* Reject Confirmation */}
      <ConfirmDialog
        isOpen={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onConfirm={handleRejectTransfer}
        title="Reject Transfer"
        message={`Are you sure you want to reject transfer ${selectedTransfer?.transfer_number}?`}
        confirmText="Reject"
        type="danger"
      />
    </div>
  );
};

export default Transfers;